/**
 * MentionSuggestion — @멘션 제안 목록 컴포넌트
 *
 * TipTap Mention extension의 suggestion 렌더러로 사용됩니다.
 * 워크스페이스 멤버 목록을 필터링하여 드롭다운 형태로 표시합니다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Trans } from '@lingui/macro';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { cn } from '../../lib/utils';
import { UserAvatar } from '../components/UserAvatar';

// ── Types ──
export interface MentionUser {
  id: string;
  name: string;
  profileUrl?: string | null;
}

// ── Suggestion List Component ──
interface MentionListProps {
  items: MentionUser[];
  command: (item: { id: string; label: string }) => void;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    // 아이템 변경 시 선택 초기화
    useEffect(() => setSelectedIndex(0), [items]);

    // 스크롤 동기화
    useEffect(() => {
      const el = listRef.current?.children[selectedIndex] as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (item) {
          command({ id: item.id, label: item.name });
        }
      },
      [items, command],
    );

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-xs text-muted-foreground">
          <Trans>No members found</Trans>
        </div>
      );
    }

    return (
      <div
        ref={listRef}
        className="bg-popover border border-border rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto min-w-[200px]"
      >
        {items.map((item, index) => (
          <button
            key={item.id}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer',
              index === selectedIndex
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground hover:bg-muted',
            )}
            onClick={() => selectItem(index)}
          >
            <UserAvatar src={item.profileUrl ?? null} name={item.name} className="size-6" />
            <span className="truncate">{item.name}</span>
          </button>
        ))}
      </div>
    );
  },
);

MentionList.displayName = 'MentionList';

// ── Suggestion Configuration Factory ──
/**
 * 멘션 suggestion 옵션을 생성합니다.
 * @param getMembers - 현재 워크스페이스 멤버 목록을 반환하는 함수
 */
export function createMentionSuggestion(
  getMembers: () => MentionUser[],
): Omit<SuggestionOptions<MentionUser>, 'editor'> {
  return {
    items: ({ query }: { query: string }) => {
      const members = getMembers();
      if (!query) return members.slice(0, 10);
      const lower = query.toLowerCase();
      return members
        .filter((m) => m.name.toLowerCase().includes(lower))
        .slice(0, 10);
    },

    render: () => {
      let component: ReactRenderer<MentionListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props: SuggestionProps<MentionUser>) => {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });

          if (!props.clientRect) return;

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
        },

        onUpdate: (props: SuggestionProps<MentionUser>) => {
          component?.updateProps(props);

          if (!props.clientRect) return;

          popup?.[0]?.setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
        },
      };
    },
  };
}

// ── Utility: JSON에서 멘션된 사용자 ID 추출 ──
/** "@전체" sentinel ID — UnifiedMentionSuggestion 과 동일 값 */
const MENTION_ALL_ID = '@all';

/**
 * TipTap JSON에서 사용자 mention 노드를 탐색하여 고유 user ID 배열을 반환합니다.
 * attrs.mentionType === 'wiki-page' 인 노드는 제외 (위키링크는 별도 함수가 처리).
 * "@전체" sentinel ID 도 제외 (개별 사용자가 아니므로) — hasMentionAll 로 확인.
 */
export function extractMentionedUserIds(json: any): string[] {
  const ids = new Set<string>();

  function walk(node: any) {
    if (!node) return;
    if (node.type === 'mention' && node.attrs?.id) {
      const mtype = node.attrs.mentionType;
      // mentionType 미정의(기존 데이터) 또는 'user'면 사용자 멘션으로 간주
      if (!mtype || mtype === 'user') {
        // "@전체" sentinel 은 개별 사용자 멘션과 분리 (mention_all 플래그로 처리)
        if (node.attrs.id !== MENTION_ALL_ID) {
          ids.add(node.attrs.id);
        }
      }
    }
    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  }

  walk(json);
  return Array.from(ids);
}

/** TipTap JSON 본문에 "@전체" mention 노드가 포함됐는지 검사 */
export function hasMentionAll(json: any): boolean {
  let found = false;
  function walk(node: any) {
    if (found || !node) return;
    if (node.type === 'mention' && node.attrs?.id === MENTION_ALL_ID) {
      const mtype = node.attrs.mentionType;
      if (!mtype || mtype === 'user') {
        found = true;
        return;
      }
    }
    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  }
  walk(json);
  return found;
}
